import { useCallback, useEffect, useState } from "react";
import { SELECTED_AUDIO_INPUT_KEY } from "config/livekit";

/**
 * Which microphone the UI should name.
 *
 * The live track is the truth: the dropdown has to show the device actually
 * feeding the session, not a stored pick the browser may never have honoured
 * — that gap is how a user ends up looking at one device while talking into
 * another. A swap is the single exception, because `restart()` is async: the
 * device the user just chose holds the label until the track reports it, or
 * until the swap fails.
 */
export default function useSelectedAudioInput(trackDeviceId) {
  const [pendingDeviceId, setPendingDeviceId] = useState(null);

  useEffect(() => {
    if (pendingDeviceId && trackDeviceId === pendingDeviceId) setPendingDeviceId(null);
  }, [pendingDeviceId, trackDeviceId]);

  const selectDeviceId = useCallback((deviceId) => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(SELECTED_AUDIO_INPUT_KEY, deviceId);
    }
    setPendingDeviceId(deviceId);
  }, []);

  const clearPendingDeviceId = useCallback(() => setPendingDeviceId(null), []);

  return [pendingDeviceId ?? trackDeviceId, selectDeviceId, clearPendingDeviceId];
}
